/**
 * Fish: X-Wing (2), Swordfish (3), Jellyfish (4).
 *
 * The same "n things confined to n slots" deduction as subsets, turned ninety
 * degrees: instead of n cells confined to n digits within one unit, it is one
 * digit confined to n crossing lines across n base lines. Whichever way the
 * digit falls, it occupies all n crossing lines, so it leaves them everywhere
 * else -- which is exactly the elimination below.
 */

import { CELLS, EMPTY, cellName, type Digit } from "../grid";
import { maskOf, popcount } from "../candidates";
import { unitName, type Constraint, type ConstraintSet } from "../constraints";
import { forEachConfinedSet, type Confinable } from "./confined";
import type { Context, Elimination, Step, Technique, TechniqueId, Tier } from "./types";

const FISH_NAME = ["", "", "X-Wing", "Swordfish", "Jellyfish"] as const;

interface Orientation {
  readonly bases: readonly Constraint[];
  readonly covers: readonly Constraint[];
  /** cell -> index into `covers`, or -1. */
  readonly coverOf: Int8Array;
}

const orientationCache = new WeakMap<ConstraintSet, Orientation[]>();

/**
 * Fish need two families of lines that each cover the grid exactly once --
 * rows against columns, and columns against rows. Boxes are deliberately not a
 * fish family: a box/line fish is just locked candidates, already handled.
 */
function orientations(cs: ConstraintSet): Orientation[] {
  let found = orientationCache.get(cs);
  if (found) return found;

  const rows = cs.constraints.filter((c) => c.kind === "row");
  const cols = cs.constraints.filter((c) => c.kind === "col");

  found = [
    [rows, cols],
    [cols, rows],
  ]
    .filter(([bases, covers]) => bases!.length > 0 && covers!.length > 0)
    .map(([bases, covers]) => {
      const coverOf = new Int8Array(CELLS).fill(-1);
      covers!.forEach((cover, index) => {
        for (const cell of cover.cells) coverOf[cell] = index;
      });
      return { bases: bases!, covers: covers!, coverOf };
    });

  orientationCache.set(cs, found);
  return found;
}

function findFish(ctx: Context, size: number, id: TechniqueId): Step | null {
  const { board, candidates, cs } = ctx;
  let result: Step | null = null;

  for (const { bases, covers, coverOf } of orientations(cs)) {
    for (let digit = 1 as Digit; digit <= 9; digit = (digit + 1) as Digit) {
      const bit = maskOf(digit);

      const items: Confinable<Constraint>[] = [];
      for (const base of bases) {
        let slots = 0;
        for (const cell of base.cells) {
          if (board[cell] === EMPTY && candidates[cell]! & bit) slots |= 1 << coverOf[cell]!;
        }
        // One position is a hidden single, not a fish; more than `size` cannot fit.
        const count = popcount(slots);
        if (count >= 2 && count <= size) items.push({ ref: base, slots });
      }
      if (items.length < size) continue;

      forEachConfinedSet(items, size, ({ items: chosenBases, slots }) => {
        const inBase = new Set<number>();
        for (const base of chosenBases) for (const cell of base.cells) inBase.add(cell);

        const eliminations: Elimination[] = [];
        const chosenCovers: Constraint[] = [];
        for (let index = 0; index < covers.length; index++) {
          if ((slots & (1 << index)) === 0) continue;
          const cover = covers[index]!;
          chosenCovers.push(cover);
          for (const cell of cover.cells) {
            if (inBase.has(cell)) continue;
            if (board[cell] === EMPTY && candidates[cell]! & bit) eliminations.push({ cell, digit });
          }
        }
        if (eliminations.length === 0) return false;

        const pattern: number[] = [];
        for (const base of chosenBases) {
          for (const cell of base.cells) {
            if ((slots & (1 << coverOf[cell]!)) && candidates[cell]! & bit) pattern.push(cell);
          }
        }

        result = {
          technique: id,
          pattern,
          digits: [digit],
          placements: [],
          eliminations,
          explanation:
            `${FISH_NAME[size]}: across ${chosenBases.map(unitName).join(", ")}, ${digit} can ` +
            `only sit in ${chosenCovers.map(unitName).join(", ")} ` +
            `(${pattern.map(cellName).join(", ")}). However those ${size} lines resolve, they ` +
            `use up ${digit} in all ${size} of the crossing lines, so ${digit} can be removed ` +
            `from those elsewhere.`,
        };
        return true;
      });

      if (result) return result;
    }
  }
  return null;
}

function fish(size: 2 | 3 | 4, id: TechniqueId, tier: Tier, ser: number): Technique {
  return {
    id,
    name: FISH_NAME[size],
    tier,
    ser,
    find: (ctx) => findFish(ctx, size, id),
  };
}

export const xWing = fish(2, "x-wing", "hard", 3.2);
export const swordfish = fish(3, "swordfish", "hard", 3.8);
export const jellyfish = fish(4, "jellyfish", "hard", 5.2);
